import { Link } from "react-router-dom";

import { Bullets, Footnote, P, Prose, ProseHeading, Record, Records, ProseTitle } from "../components/prose";
import { mono } from "../components/primitives";
import { CONTACT, LAST_UPDATED } from "../lib/legal";

/**
 * Every claim on this page is checked against the schema and the request path,
 * not against what the design intended. Two of them were false when this page
 * was first written — the raw address outliving a sign-in attempt, and
 * subscriber rows never being pruned — and the code was changed rather than the
 * sentence.
 */
export function Privacy() {
  return (
    <Prose>
      <ProseTitle>Privacy</ProseTitle>
      <P>
        Diditake.it is a place to write packing templates and publish them as a link. There is no
        email address, no name and no password: an account is an opaque id, created the first time
        the app on your phone registers itself.
      </P>
      <P>
        There is no analytics, no advertising, no third-party script and no external font. Every
        byte this page loads comes from this domain.
      </P>

      <ProseHeading>What is stored, and for how long</ProseHeading>
      <Records>
        <Record
          what="An account id and the date it was created"
          why="To own your collections"
          kept="Until you delete the account"
        />
        <Record
          what={
            <>
              For each authorized phone: a SHA-256 of its access token — never the token — plus the
              platform and model it reports (&ldquo;iOS 26.2&rdquo;, &ldquo;iPhone 17&rdquo;), and
              when it was added and last seen
            </>
          }
          why="So you can see which phones are signed in, and revoke one"
          kept="Until you revoke that phone or delete the account"
        />
        <Record
          what={
            <>
              For each browser session: a SHA-256 of the session token, the browser&rsquo;s
              User-Agent string, and a salted hash of the address it signed in from
            </>
          }
          why="To keep you signed in, and to let you end a session you do not recognise"
          kept="30 days after its last use, or until you revoke it"
        />
        <Record
          what={
            <>
              For each sign-in attempt: hashes of the one-time code, a short browser description,
              and the address the request came from
            </>
          }
          why="So the app can show you which browser and address is asking, before you authorize it"
          kept={
            <>
              The address in readable form only while the attempt is open — cleared the moment you
              confirm. An attempt nobody answers expires after two minutes, and its row is deleted
              within a day
            </>
          }
        />
        <Record
          what={
            <>
              Your recovery code: its first group in clear as a lookup handle, the rest as a bcrypt
              hash
            </>
          }
          why="So a code you type can be checked without the code being stored"
          kept="Replaced when you generate a new one; deleted with the account"
        />
        <Record
          what="Your content: items, templates, rules, and the exact bytes of every published revision"
          why="It is the thing the service is for"
          kept={
            <>
              The 30 most recent revisions per collection, plus the published one; the rest are
              pruned when you publish
            </>
          }
        />
        <Record
          what={
            <>
              For each published collection: a salted hash of each address that downloaded it, and
              when it last did
            </>
          }
          why="To show the author roughly how many people a change reaches, before they publish it"
          kept="30 days from that address’s last request"
        />
      </Records>

      <ProseHeading>Addresses</ProseHeading>
      <P>
        Where an address is kept it is kept as a salted SHA-256, and the salt is a secret held by
        the server. A hash cannot be turned back into an address by trying every address, which is
        what makes it different from writing the address down.
      </P>
      <P>
        The one exception is above: while a sign-in attempt is open, the readable address is stored
        so that the phone can show you where the request came from. That is the whole reason it
        exists, and it does not outlive the attempt.
      </P>

      <ProseHeading>Access codes</ProseHeading>
      <P>
        A collection&rsquo;s access code is stored as text, because the server has to compare it
        with what a subscriber presents. Codes travel in the fragment of a share link — the part
        after <span className={mono}>#</span>, which browsers do not send to any server. Treat a
        share link as the secret it is.
      </P>

      <ProseHeading>What subscribers send</ProseHeading>
      <P>
        A phone that has subscribed to a collection fetches its bundle over HTTPS, at most once
        every 15 minutes. That request carries no account, no device id and nothing about what the
        person packed — only the access code, if the collection has one. What the server records is
        the salted-hash row in the table above.
      </P>

      <ProseHeading>Server logs</ProseHeading>
      <P>
        The service writes one structured line per request to the container&rsquo;s log: the method,
        the path, the status, the browser&rsquo;s User-Agent, and the account id where the request
        had one. No request bodies, no access codes, no template content. Logs rotate at three files
        of ten megabytes, so the oldest lines fall off the end within days.
      </P>

      <ProseHeading>Cookies</ProseHeading>
      <P>Three, all of them necessary to sign you in and keep you signed in:</P>
      <Bullets>
        <li>
          <span className={mono}>dit_session</span> — your session token. HTTP-only, so no script
          can read it.
        </li>
        <li>
          <span className={mono}>dit_csrf</span> — readable on purpose. The page echoes it in a
          header, which a page on another site cannot do; that is what stops a cross-site request
          from acting as you.
        </li>
        <li>
          <span className={mono}>dit_pairing</span> — lives for the two minutes of a sign-in
          attempt, so only the browser that asked for a code can claim the session.
        </li>
      </Bullets>
      <P>
        There is no consent banner, because none of these is for tracking: they are strictly
        necessary to provide the thing you asked for, which is the one case where consent is not
        required. If this service ever wants a cookie that is not, it will ask first — and this
        paragraph will change.
      </P>

      <ProseHeading>Deleting everything</ProseHeading>
      <P>
        <Link to="/account">Settings</Link> → Danger zone → Delete everything. It is a real delete:
        the account row goes and every device, session, sign-in attempt, item, collection and
        published revision goes with it. Nothing is archived.
      </P>
      <P>
        Links to collections you had published start answering{" "}
        <span className={mono}>410 Gone</span>, so a subscriber&rsquo;s app tells them the author
        deleted the source and stops asking. The only thing kept is the fact that those addresses
        were once used, so they can keep saying that. Templates already downloaded to a
        subscriber&rsquo;s phone stay there, as local copies — they are on their device, not ours.
      </P>

      <ProseHeading>Asking about any of this</ProseHeading>
      <P>
        Write to <span className={mono}>{CONTACT}</span>. There is no account for you to name, so
        say which collection or share link you mean.
      </P>

      <Footnote>
        Last updated {LAST_UPDATED}. See also the <Link to="/terms">terms</Link>.
      </Footnote>
    </Prose>
  );
}
